import React, { useState, useEffect } from "react";
import { Clear, Search, Business, MiscellaneousServices, LocationOn } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, TextField, IconButton, Button, Chip,
    CircularProgress, Fade, Zoom, Tab, Tabs, Avatar
} from "@mui/material";
import Select from 'react-select';
import "swiper/css";
import "./../Utils/styles.css";
import { allcampuses } from "../Utils/arrays";
import axios from "axios";
import { Fetchbusinessesurl, Fetchservicesurl } from "../Utils/urls";
import Shuffle from "../Utils/shuffle";
import { FaRegMap, FaStar, FaArrowRight, FaStore, FaTools } from "react-icons/fa";

const BRAND = '#900f20';
const BRAND_DARK = '#670600';

// ─── Business Card ──────────────────────────────────────────────────────────
const BusinessCard = ({ val, onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <Fade in timeout={400}>
            <Paper
                elevation={0}
                onClick={onClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    width: { xs: '100%', sm: '48%', md: '31%', lg: '18%' },
                    mb: 3, borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                    border: '1px solid rgba(0,0,0,0.04)', bgcolor: 'white',
                    transition: 'all 0.4s cubic-bezier(0.175,0.885,0.32,1.275)',
                    boxShadow: hovered ? '0 20px 40px rgba(0,0,0,0.08)' : '0 4px 20px rgba(0,0,0,0.03)',
                    transform: hovered ? 'translateY(-8px)' : 'none',
                    display: 'flex', flexDirection: 'column',
                }}
            >
                {/* Image */}
                <Box sx={{ height: 200, overflow: 'hidden', position: 'relative', bgcolor: '#f0f0f0' }}>
                    {val?.url1 ? (
                        <Box
                            component="img"
                            src={val.url1}
                            alt={val?.bname}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s', transform: hovered ? 'scale(1.06)' : 'scale(1)' }}
                        />
                    ) : (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(144,15,32,0.05)' }}>
                            <FaStore size={48} color="rgba(144,15,32,0.2)" />
                        </Box>
                    )}
                    {/* Service badge */}
                    {val?.service && (
                        <Chip
                            label={val.service}
                            size="small"
                            sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', fontWeight: 700, fontSize: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                        />
                    )}
                </Box>

                {/* Content */}
                <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1a1a1a', mb: 0.75, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {val?.bname}
                    </Typography>
                    {val?.description && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {val.description}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn sx={{ fontSize: 16, color: '#a4b0be' }} />
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>{val?.campus || 'Campus'}</Typography>
                        </Box>
                        {hovered && (
                            <Typography variant="caption" sx={{ color: BRAND, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                View <FaArrowRight size={12} />
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Fade>
    );
};

// ─── Service Item Card ───────────────────────────────────────────────────────
const ServiceCard = ({ val, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const priceFormatted = val?.price ? parseInt(val.price).toLocaleString() : null;

    return (
        <Fade in timeout={400}>
            <Paper
                elevation={0}
                onClick={onClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    width: { xs: '100%', sm: '48%', md: '31%', lg: '23%' },
                    mb: 3, borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                    border: '1px solid rgba(0,0,0,0.04)', bgcolor: 'white',
                    transition: 'all 0.4s cubic-bezier(0.175,0.885,0.32,1.275)',
                    boxShadow: hovered ? '0 20px 40px rgba(0,0,0,0.08)' : '0 4px 20px rgba(0,0,0,0.03)',
                    transform: hovered ? 'translateY(-8px)' : 'none',
                    display: 'flex', flexDirection: 'column',
                }}
            >
                {/* Image */}
                <Box sx={{ height: 200, overflow: 'hidden', position: 'relative', bgcolor: '#f0f0f0' }}>
                    <Box
                        component="img"
                        src={val?.url || require("./../../assets/common/image2.png")}
                        alt={val?.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s', transform: hovered ? 'scale(1.06)' : 'scale(1)' }}
                    />
                    {priceFormatted && (
                        <Box sx={{ position: 'absolute', bottom: 12, right: 12, bgcolor: BRAND, color: 'white', px: 1.5, py: 0.5, borderRadius: '10px', fontWeight: 800, fontSize: '0.85rem', boxShadow: '0 4px 12px rgba(144,15,32,0.3)' }}>
                            Ksh {priceFormatted}
                        </Box>
                    )}
                </Box>

                {/* Content */}
                <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1a1a1a', mb: 0.75, lineHeight: 1.3 }}>
                        {val?.name}
                    </Typography>
                    {val?.description && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {val.description}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {[...Array(5)].map((_, i) => <FaStar key={i} size={12} color="#f9ca24" />)}
                        </Box>
                        {hovered && (
                            <Typography variant="caption" sx={{ color: BRAND, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                View <FaArrowRight size={12} />
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Fade>
    );
};

// ─── Skeleton Loader Card ────────────────────────────────────────────────────
const SkeletonCard = () => (
    <Paper elevation={0} sx={{ width: { xs: '100%', sm: '48%', md: '31%', lg: '23%' }, mb: 3, height: 320, borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
        <CircularProgress size={28} sx={{ color: BRAND }} />
    </Paper>
);

// ─── Main component ──────────────────────────────────────────────────────────
const Allservices = () => {
    const [hint, sethint] = useState('');
    const [campusfilter, setcampusfilter] = useState('Chuka university');
    const [activeTab, setactiveTab] = useState(0); // 0 = Businesses, 1 = Services

    const [progress, setprogress] = useState(true);
    const [display, setdisplay] = useState(false);
    const [showservices, setshowservices] = useState(false);
    const [error, seterror] = useState('');

    const [sortedbusinesses, setsortedbusinesses] = useState([]);
    const [sortedservices, setsortedservices] = useState([]);

    const [sortedfirstslot, setsortedfirstslot] = useState([]);
    const [sortedsecondslot, setsortedsecondslot] = useState([]);
    const [businesses, setbusinesses] = useState([]);
    const [services, setservices] = useState([]);

    const navigate = useNavigate();

    useEffect(() => {
        fetchallbusinesses();
    }, []);

    const selectStyles = {
        control: (base, state) => ({
            ...base, borderRadius: 12, border: `1px solid ${state.isFocused ? BRAND : '#e0e0e0'}`, boxShadow: state.isFocused ? `0 0 0 2px rgba(144,15,32,0.1)` : 'none', '&:hover': { borderColor: BRAND }, minHeight: 44, fontSize: 14,
        }),
        option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? BRAND : state.isFocused ? 'rgba(144,15,32,0.05)' : 'white', color: state.isSelected ? 'white' : '#2d3436', fontWeight: state.isSelected ? 700 : 400 }),
        indicatorSeparator: () => ({ display: 'none' }),
    };

    const chunkAndSet = (data, setFirst, setSecond, setDisplay) => {
        const perChunk = Math.max(1, Math.round(data.length / 2));
        const result = data.reduce((acc, item, idx) => {
            const ci = Math.floor(idx / perChunk);
            if (!acc[ci]) acc[ci] = [];
            acc[ci].push(item);
            return acc;
        }, []);
        setsortedfirstslot(result[0] || []);
        setsortedsecondslot(result[1] || []);
        setFirst(result[0] || []);
        if (setDisplay) setDisplay(true);
    };

    // ── Businesses ──────────────────────────────────────────────────────────
    const fetchallbusinesses = (hint) => {
        setprogress(true);
        setdisplay(false);
        seterror(null);
        setshowservices(false);
        axios.post(Fetchbusinessesurl, {
            hint: hint || '',
            campus: campusfilter || "Chuka university",
        }).then(response => {
            setprogress(false);
            if (!response.data.message && Array.isArray(response.data) && response.data.length > 0) {
                setbusinesses(response.data);
                chunkAndSet(response.data, setsortedbusinesses, null, setdisplay);
            } else {
                seterror(response.data?.message || "No businesses found.");
            }
        }).catch(() => { setprogress(false); seterror("Error loading businesses. Try again."); });
    };

    // ── Services ────────────────────────────────────────────────────────────
    const fetchallservices = (hint) => {
        setprogress(true);
        setdisplay(false);
        seterror(null);
        setshowservices(false);
        axios.post(Fetchservicesurl, {
            hint: hint || '',
            campus: campusfilter || "Chuka university",
        }).then(response => {
            setprogress(false);
            if (!response.data.message && Array.isArray(response.data) && response.data.length > 0) {
                setservices(response.data);
                setsortedfirstslot(response.data);
                setsortedservices(response.data);
                setshowservices(true);
            } else {
                seterror(response.data?.message || "No services found.");
            }
        }).catch(() => { setprogress(false); seterror("Error loading services. Try again."); });
    };

    const handleTabChange = (_, newValue) => {
        setactiveTab(newValue);
        sethint('');
        seterror(null);
        if (newValue === 0) {
            fetchallbusinesses();
        } else {
            fetchallservices();
        }
    };

    const handleSearch = (value) => {
        sethint(value);
        if (activeTab === 0) {
            fetchallbusinesses(value);
        } else {
            fetchallservices(value);
        }
    };

    const paginationSlots = [
        { id: 1, data: sortedfirstslot },
        { id: 2, data: sortedsecondslot },
    ].filter(s => s.data && s.data.length > 0);

    const setActivePage = (slot) => {
        if (activeTab === 0) setsortedbusinesses(slot);
        else setsortedservices(slot);
    };

    const currentData = activeTab === 0 ? sortedbusinesses : sortedservices;
    const isActiveSlot = (data) => (activeTab === 0 ? sortedbusinesses : sortedservices) === data;

    return (
        <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pb: 10, width: "95%", margin: "auto" }}>

            {/* ── Header ── */}
            <Box sx={{ px: { xs: 2, md: 4 }, pt: 4, pb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.5px', mb: 0.75 }}>
                    Discover Eaziserve
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 600 }}>
                    Find local businesses and services near your campus — from tutors to laundry and everything in between.
                </Typography>

                {/* Search + Campus Row */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'flex-start' }}>
                    {/* Search Bar */}
                    <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', pl: 2, borderRadius: '16px', bgcolor: 'white', boxShadow: '0 8px 32px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.03)', flex: '1 1 300px', maxWidth: 500, height: 52 }}>
                        <Search sx={{ color: 'text.disabled', mr: 1, flexShrink: 0 }} />
                        <TextField
                            fullWidth
                            placeholder={activeTab === 0 ? "Search businesses..." : "Search services..."}
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            value={hint}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        {hint && (
                            <IconButton size="small" onClick={() => handleSearch('')} sx={{ mr: 1 }}>
                                <Clear fontSize="small" />
                            </IconButton>
                        )}
                    </Paper>

                    {/* Campus Filter */}
                    <Box sx={{ minWidth: 240, flex: '0 0 240px' }}>
                        <Select
                            styles={selectStyles}
                            options={allcampuses}
                            placeholder="Filter by campus..."
                            onChange={e => {
                                setcampusfilter(e.value);
                                if (activeTab === 0) fetchallbusinesses(hint);
                                else fetchallservices(hint);
                            }}
                        />
                    </Box>
                </Box>

                {/* Tab Switcher */}
                <Box sx={{ display: 'inline-flex', bgcolor: 'white', borderRadius: '16px', p: 0.75, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                    {[
                        { label: 'Businesses', icon: <FaStore size={16} /> },
                        { label: 'Services', icon: <FaTools size={16} /> },
                    ].map((tab, i) => (
                        <Box
                            key={i}
                            onClick={() => handleTabChange(null, i)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.25,
                                borderRadius: '12px', cursor: 'pointer', transition: 'all 0.25s ease',
                                bgcolor: activeTab === i ? BRAND : 'transparent',
                                color: activeTab === i ? 'white' : '#666',
                                fontWeight: activeTab === i ? 700 : 500,
                                fontSize: '0.9rem',
                                boxShadow: activeTab === i ? '0 4px 12px rgba(144,15,32,0.25)' : 'none',
                            }}
                        >
                            {tab.icon} {tab.label}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ── Content ── */}
            <Box sx={{ px: { xs: 2, md: 4 } }}>
                {/* Error */}
                {error && !progress && (
                    <Fade in>
                        <Box sx={{ bgcolor: 'error.light', color: 'error.main', p: 2, borderRadius: '12px', mb: 3, display: 'inline-block' }}>
                            <Typography variant="body2" fontWeight={600}>{error}</Typography>
                        </Box>
                    </Fade>
                )}

                {/* Count */}
                {(display || showservices) && (
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 3 }}>
                        {currentData.length} {activeTab === 0 ? 'businesses' : 'services'} found
                    </Typography>
                )}

                {/* Grid */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: '2%' } }}>
                    {/* Loading skeletons */}
                    {progress && [...Array(8)].map((_, i) => <SkeletonCard key={i} />)}

                    {/* Business cards */}
                    {!progress && display && activeTab === 0 && sortedbusinesses?.map((val, i) => (
                        <BusinessCard
                            key={i}
                            val={val}
                            onClick={() => navigate(`/nav/viewsinglebusiness/${val.email}`)}
                        />
                    ))}

                    {/* Service cards */}
                    {!progress && showservices && activeTab === 1 && sortedservices?.map((val, i) => (
                        <ServiceCard
                            key={i}
                            val={val}
                            onClick={() => navigate('/nav/viewsingleservice', { state: { item: val } })}
                        />
                    ))}
                </Box>

                {/* Pagination */}
                {paginationSlots.length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 6 }}>
                        <Paper elevation={0} sx={{ display: 'inline-flex', p: 0.75, borderRadius: '24px', bgcolor: 'white', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                            {paginationSlots.map((slot) => {
                                const active = isActiveSlot(slot.data);
                                return (
                                    <Box key={slot.id} onClick={() => setActivePage(slot.data)}
                                        sx={{
                                            width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', mx: 0.5, transition: 'all 0.3s',
                                            bgcolor: active ? BRAND : 'transparent',
                                            color: active ? 'white' : 'text.secondary',
                                            fontWeight: active ? 700 : 500,
                                            '&:hover': { bgcolor: active ? BRAND : 'rgba(144,15,32,0.08)', color: active ? 'white' : BRAND }
                                        }}
                                    >
                                        {slot.id}
                                    </Box>
                                );
                            })}
                        </Paper>
                    </Box>
                )}

                {/* Empty state */}
                {!progress && !display && !showservices && !error && (
                    <Box sx={{ textAlign: 'center', py: 10 }}>
                        <Box sx={{ fontSize: 64, mb: 2 }}>🔍</Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>No results found</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>Try a different search term or change the campus filter.</Typography>
                        <Button onClick={() => { sethint(''); if (activeTab === 0) fetchallbusinesses(); else fetchallservices(); }}
                            variant="outlined"
                            sx={{ borderColor: BRAND, color: BRAND, borderRadius: '12px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: 'rgba(144,15,32,0.04)' } }}
                        >
                            Clear Search
                        </Button>
                    </Box>
                )}
            </Box>

            {/* ── Floating Add Listing CTA ── */}
            <Zoom in style={{ transitionDelay: '400ms' }}>
                <Box
                    onClick={() => navigate('/nav/mybusinesses')}
                    sx={{
                        position: 'fixed', bottom: 36, right: 36,
                        bgcolor: BRAND, color: 'white',
                        px: 3, py: 1.8, borderRadius: '20px',
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        cursor: 'pointer', boxShadow: '0 8px 28px rgba(144,15,32,0.35)', zIndex: 1000,
                        fontWeight: 700, fontSize: '0.9rem',
                        transition: 'all 0.3s ease',
                        '&:hover': { bgcolor: BRAND_DARK, transform: 'translateY(-4px)', boxShadow: '0 12px 36px rgba(144,15,32,0.45)' }
                    }}
                >
                    + List Your Business
                </Box>
            </Zoom>
        </Box>
    );
};

export default Allservices;